import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useHistory, useLocation } from 'react-router-dom'

import queryString from 'query-string'

import { toBase64 } from 'src/lib/js/base64'
import { useQueryString } from 'src/lib/js/hooks/useQueryString'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import {
  TOAST_LOCAL_CATERING_SEARCH_PATH,
  TOAST_LOCAL_SEARCH_PATH,
  TOAST_LOCAL_VENUES_SEARCH_PATH
} from 'src/public/js/siteUtilities'

import { useFlags } from 'shared/components/common/feature_flags/useFlags'

import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import Footer from 'public/components/toast_local/common/Footer'
import CuisineFilters from 'public/components/toast_local/common/cuisine_filters/CuisineFilters'
import LocationDropDown, {
  DropdownSearchMode
} from 'public/components/toast_local/common/location_dropdown/LocationDropdown'
import Nav from 'public/components/toast_local/common/nav/Nav'
import { rxLocationToQueryString } from 'public/components/toast_local/common/searchUtils'
import SearchBar from 'public/components/toast_local/common/search_bar/SearchBar'
import PaginatedRXs from 'public/components/toast_local/paginated_rxs/PaginatedRXs'
import { FilterToggle } from 'public/components/toast_local/rx_search/FilterToggle'
import {
  MSAS,
  msaToUrlPath
} from 'public/components/toast_local/splash_page/msas.data'

import CateringDiscover from './catering_discover/CateringDiscover'
import { useShowAcceptsToastCashFilter } from './toast-cash-accepted-helpers'
import { geoFilterSet } from './trackingUtils'
import { t } from 'src/translations'

export enum DiningOptionFilters {
  DineIn = 'dineIn',
  TakeoutOrDelivery = 'takeoutOrDelivery'
}

const getCityMatchFromCateringPath = (pathname: string) => {
  if (
    pathname.includes(TOAST_LOCAL_CATERING_SEARCH_PATH) &&
    pathname !== TOAST_LOCAL_CATERING_SEARCH_PATH
  ) {
    return MSAS.find((msa) => pathname.includes(msaToUrlPath(msa)))
  }
  return undefined
}

export const getSearchPathForMode = (mode: DropdownSearchMode): string => {
  switch (mode) {
    case 'Catering':
      return TOAST_LOCAL_CATERING_SEARCH_PATH
    case 'Venues':
      return TOAST_LOCAL_VENUES_SEARCH_PATH
    case 'Restaurants':
    default:
      return TOAST_LOCAL_SEARCH_PATH
  }
}

const RxSearch = ({
  mapView,
  mode = 'Restaurants'
}: {
  mapView?: boolean
  mode?: DropdownSearchMode
}) => {
  const parsedQS = useQueryString(['filters'], ['filters'])
  const history = useHistory()
  const { search, pathname } = useLocation()
  const [resetString, setResetString] = useState(false)
  const searchPageLandingViewedRef = useRef(false)
  const { loadingCustomer } = useCustomer()
  const { track } = useThrottledTracker()

  const { nvsCateringDiscoveryToastLocal, nvsMarketplaceLeadForms } = useFlags()

  const baseUrl = getSearchPathForMode(mode)

  useEffect(() => {
    // If its the catering search path, check it against the supported cities
    if (!search) {
      const cityMatchFromPath = getCityMatchFromCateringPath(pathname)
      if (cityMatchFromPath) {
        history.replace(
          `${pathname}?${rxLocationToQueryString(cityMatchFromPath)}`
        )
      }
    }
    // Run only on mount to check pathname once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Redirect if catering discover feature is off
    if (
      pathname === TOAST_LOCAL_CATERING_SEARCH_PATH &&
      nvsCateringDiscoveryToastLocal === false
    ) {
      history.replace(TOAST_LOCAL_SEARCH_PATH)
      return
    }
    // Redirect if venues feature is off
    if (
      pathname === TOAST_LOCAL_VENUES_SEARCH_PATH &&
      nvsMarketplaceLeadForms === false
    ) {
      history.replace(TOAST_LOCAL_SEARCH_PATH)
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nvsCateringDiscoveryToastLocal, nvsMarketplaceLeadForms])

  const searchLocationForCatering = useMemo(() => {
    if (parsedQS?.filters?.userLat && parsedQS?.filters?.userLong) {
      return { lat: parsedQS.filters.userLat, long: parsedQS.filters.userLong }
    }

    const cityMatchFromPath = getCityMatchFromCateringPath(pathname)
    if (cityMatchFromPath) {
      return {
        lat: cityMatchFromPath.latitude,
        long: cityMatchFromPath.longitude
      }
    }

    return undefined
  }, [parsedQS?.filters, pathname])

  // Only track on initial landing experience (ampltiude budget control) via ref.
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      !searchPageLandingViewedRef.current &&
      !loadingCustomer &&
      mode === 'Restaurants'
    ) {
      track(TrackingEvents.LOCAL_SEARCH_VIEW, {
        cuisine: parsedQS?.filters?.cuisine || null,
        geoFilter: geoFilterSet(parsedQS?.filters),
        search: !!parsedQS?.filters?.search
      })
      searchPageLandingViewedRef.current = true
    }
  }, [parsedQS?.filters, track, loadingCustomer, mode])

  const onClickCuisineFilter = (event: React.MouseEvent, cuisine: string) => {
    // The default behavior of cuisine filters is to link to another page. This prevents that.
    event.preventDefault()
    if (parsedQS.filters && parsedQS.filters.cuisine === cuisine) {
      history.push(
        `${baseUrl}?${queryString.stringify({
          ...parsedQS,
          filters: toBase64(
            JSON.stringify({ ...parsedQS.filters, cuisine: null })
          )
        })}`
      )
    } else {
      history.push(
        `${baseUrl}?${queryString.stringify({
          ...parsedQS,
          filters: toBase64(JSON.stringify({ ...parsedQS.filters, cuisine }))
        })}`
      )
    }
  }

  const onLocationChange = (
    street: string,
    neighborhood: string,
    city: string,
    state: string,
    lat: number,
    long: number
  ) => {
    history.push(
      `${baseUrl}?${queryString.stringify({
        ...parsedQS,
        filters: toBase64(
          JSON.stringify({
            ...parsedQS.filters,
            userStreet: street || '',
            userNeighborhood: neighborhood || '',
            userCity: city,
            userState: state,
            userLat: lat,
            userLong: long
          })
        )
      })}`
    )
  }

  const showAcceptsToastCashFilterToggle = useShowAcceptsToastCashFilter()

  // when we show the dining option filter, we want to default to takeout
  useEffect(() => {
    if (showAcceptsToastCashFilterToggle) {
      if (parsedQS.filters?.diningOption === undefined) {
        history.push(
          `${baseUrl}?${queryString.stringify({
            ...parsedQS,
            filters: toBase64(
              JSON.stringify({
                ...parsedQS.filters,
                diningOption: DiningOptionFilters.TakeoutOrDelivery
              })
            )
          })}`
        )
      }
    }
  }, [history, parsedQS, showAcceptsToastCashFilterToggle, baseUrl])

  const onChangeView = (mode: DropdownSearchMode) => {
    const newPath = getSearchPathForMode(mode)
    history.push(`${newPath}${search}`)
  }

  const locationFilters = (
    <div className='toggles-container'>
      {/* Force presence based upon presence in QS */}
      {showAcceptsToastCashFilterToggle ? (
        <div className='accepts-toggles'>
          <FilterToggle
            name={DiningOptionFilters.TakeoutOrDelivery}
            key={DiningOptionFilters.TakeoutOrDelivery}
            displayName={t('rx-search.takeout')}
            iconName='pick-up'
            checkedIconName='pick-up-white'
            checked={
              parsedQS.filters.diningOption ===
                DiningOptionFilters.TakeoutOrDelivery ||
              !parsedQS.filters.diningOption
            }
            type='radio'
            setChecked={() => {
              history.push(
                `/local/search?${queryString.stringify({
                  ...parsedQS,
                  filters: toBase64(
                    JSON.stringify({
                      ...parsedQS.filters,
                      diningOption: DiningOptionFilters.TakeoutOrDelivery
                    })
                  )
                })}`
              )
            }}
          />
          <FilterToggle
            name={DiningOptionFilters.DineIn}
            key={DiningOptionFilters.DineIn}
            displayName={t('rx-search.dine-in')}
            iconName='table'
            checkedIconName='table-white'
            checked={
              parsedQS.filters.diningOption === DiningOptionFilters.DineIn ||
              !parsedQS.filters.diningOption
            }
            type='radio'
            setChecked={() => {
              history.push(
                `/local/search?${queryString.stringify({
                  ...parsedQS,
                  filters: toBase64(
                    JSON.stringify({
                      ...parsedQS.filters,
                      diningOption: DiningOptionFilters.DineIn
                    })
                  )
                })}`
              )
            }}
          />
          <FilterToggle
            name='acceptsToastCash'
            key='acceptsToastCash'
            displayName={t('rx-search.toast-cash')}
            iconName='toast-cash'
            checkedIconName='toast-cash-white'
            checked={parsedQS.filters.acceptsToastCash ?? false}
            type='checkbox'
            setChecked={(checked) => {
              history.push(
                `${baseUrl}?${queryString.stringify({
                  ...parsedQS,
                  filters: toBase64(
                    JSON.stringify({
                      ...parsedQS.filters,
                      acceptsToastCash: checked
                    })
                  )
                })}`
              )
            }}
          />
          <FilterToggle
            name='filterOffers'
            key='filterOffers'
            displayName={t('rx-search.offers')}
            iconName='tag'
            checkedIconName='tag-white'
            checked={parsedQS.filters.hasOffers ?? false}
            type='checkbox'
            setChecked={(checked) => {
              history.push(
                `${baseUrl}?${queryString.stringify({
                  ...parsedQS,
                  filters: toBase64(
                    JSON.stringify({
                      ...parsedQS.filters,
                      hasOffers: checked
                    })
                  )
                })}`
              )
            }}
          />
        </div>
      ) : null}
    </div>
  )

  return (
    <div className='defaultTemplate rx-search-container'>
      <Nav>
        <LocationDropDown
          mode={mode}
          parsedQS={parsedQS}
          street={parsedQS.filters && parsedQS.filters.userStreet}
          neighborhood={parsedQS.filters && parsedQS.filters.userNeighborhood}
          city={parsedQS.filters?.userCity}
          state={parsedQS.filters?.userState}
          onDropdownChange={onChangeView}
          onLocationSelected={onLocationChange}
        />
      </Nav>
      <Helmet>
        {/* Prevent crawlers from making expensive searches that slow our service. */}
        <meta name='robots' content='noindex, nofollow' />
      </Helmet>
      {mode === 'Restaurants' && (
        <div className='rx-search'>
          <div className='filters-container'>
            <div className='page-contents'>
              <CuisineFilters
                title={locationFilters}
                citySubPath=''
                selectedCuisine={parsedQS.filters && parsedQS.filters.cuisine}
                onClickFilter={onClickCuisineFilter}
              />
              <div className='rx-filters'>
                <div className='rx-filter'>
                  <SearchBar
                    placeholder={t('rx-search.placeholder')}
                    className='small'
                    resetString={resetString}
                    onStringReset={() => setResetString(false)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='page-contents'>
            <PaginatedRXs utmContent='search_oo' mapView={mapView} />
            <Footer />
          </div>
        </div>
      )}
      {nvsCateringDiscoveryToastLocal && (
        <CateringDiscover
          searchLocation={searchLocationForCatering}
          mode={mode}
        />
      )}
    </div>
  )
}

export default RxSearch
